
import Vue from "vue";
import ComponentHeader from "@/components/componentNewHeader/index.vue";
import {
    fetchMarketPoliciesList,
    fetchOrderPolicies,
    upsertOrderPolicies,
} from "@/services/orderPolicies";
import {
    COD_REFUND_METHOD_OPTIONS,
    DEFAULT_POLICY,
    ORDER_STATUSES,
    REFUND_METHOD_OPTIONS,
    SHIPPING_PAYER_OPTIONS,
} from "@/views/setup/return-policy/policy-constants";
import type { PolicyPayload } from "@/views/setup/return-policy/types";
import {
    clonePolicy,
    hydratePolicy,
    normalizeTags,
    serializePolicy,
    toApiPayload,
    validatePolicy,
} from "@/views/setup/return-policy/utils";

export default Vue.extend({
    name: "PolicyConfigV2",
    components: { ComponentHeader },
    data() {
        return {
            tab: 0,
            loading: false,
            saving: false,
            policy: clonePolicy(DEFAULT_POLICY),
            initialSnapshot: "",
            marketListLoading: false,
            marketNameOptions: [] as string[],
            showMarketModal: false,
            marketDraftIndex: -1,
            marketDraft: {
                marketName: "",
                conditions: "",
                overrideCancellation: false,
                overrideReturn: false,
                overrideReplacement: false,
                overrideRefund: false,
                cancellation: clonePolicy(DEFAULT_POLICY.cancellation),
                return: clonePolicy(DEFAULT_POLICY.return),
                replacement: clonePolicy(DEFAULT_POLICY.replacement),
                refund: clonePolicy(DEFAULT_POLICY.refund),
            },
            marketNameError: "",
            ORDER_STATUSES,
            SHIPPING_PAYER_OPTIONS,
            REFUND_METHOD_OPTIONS,
            COD_REFUND_METHOD_OPTIONS,
            DEFAULT_POLICY,
        };
    },
    computed: {
        validation() {
            return validatePolicy(this.policy as any);
        },
        canSave(): boolean {
            return this.validation.isValid && this.isDirty && !this.saving;
        },
        isDirty(): boolean {
            return serializePolicy(this.policy as any) !== this.initialSnapshot;
        },
        marketNameItems(): string[] {
            const currentName = `${this.marketDraft.marketName || ""}`.trim();
            const options = Array.isArray(this.marketNameOptions)
                ? [...this.marketNameOptions]
                : [];
            if (
                currentName &&
                !options.some(
                    (name) => name.toLowerCase() === currentName.toLowerCase(),
                )
            ) {
                options.push(currentName);
            }
            return options;
        },
    },
    mounted() {
        this.loadPolicy();
        this.loadMarketNameOptions();
        window.addEventListener("beforeunload", this.onBeforeUnload);
    },
    beforeDestroy() {
        window.removeEventListener("beforeunload", this.onBeforeUnload);
    },
    methods: {
        normalizeTags,
        errorFor(path: string) {
            return this.validation.errors[path]
                ? [this.validation.errors[path]]
                : [];
        },
        onBeforeUnload(event: BeforeUnloadEvent) {
            if (this.isDirty) {
                event.preventDefault();
                event.returnValue = "";
            }
        },
        async loadPolicy() {
            this.loading = true;
            try {
                const res = await fetchOrderPolicies();
                const body = res && res.data;
                let payload: PolicyPayload | null = null;
                if (body && typeof body === "object") {
                    const envelope = body as {
                        success?: boolean;
                        data?: unknown;
                    };
                    if (
                        envelope.success === true &&
                        envelope.data != null &&
                        typeof envelope.data === "object"
                    ) {
                        payload = envelope.data as PolicyPayload;
                    } else {
                        payload = body as PolicyPayload;
                    }
                }
                this.policy = hydratePolicy(payload);
                this.initialSnapshot = serializePolicy(this.policy as any);
            } catch (error) {
                this.policy = clonePolicy(DEFAULT_POLICY);
                this.initialSnapshot = serializePolicy(this.policy as any);
                (this as any).$toasted.error(
                    "Unable to fetch policy. Using defaults.",
                );
            } finally {
                this.loading = false;
            }
        },
        async loadMarketNameOptions() {
            this.marketListLoading = true;
            try {
                const res = await fetchMarketPoliciesList();
                const raw =
                    (res &&
                        res.data &&
                        (res.data.response || res.data.data || res.data)) ||
                    [];
                const list = Array.isArray(raw) ? raw : [];
                const names = list
                    .map((entry: any) => {
                        if (!entry) return "";
                        if (typeof entry === "string") return entry.trim();
                        return `${entry.marketName || entry.name || entry.label || entry.value || ""}`.trim();
                    })
                    .filter((name: string) => Boolean(name));
                this.marketNameOptions = [...new Set(names)];
            } catch (error) {
                this.marketNameOptions = [];
                (this as any).$toasted.error(
                    "Unable to fetch market list for dropdown.",
                );
            } finally {
                this.marketListLoading = false;
            }
        },
        async savePolicy() {
            if (!this.validation.isValid) {
                (this as any).$toasted.error(
                    "Please fix validation errors before saving.",
                );
                return;
            }
            this.saving = true;
            try {
                const payload = toApiPayload(this.policy as any);
                await upsertOrderPolicies(payload as any);
                this.policy = hydratePolicy(payload as any);
                this.initialSnapshot = serializePolicy(this.policy as any);
                (this as any).$toasted.success("Policy saved successfully.");
            } catch (error) {
                (this as any).$toasted.error("Unable to save policy.");
            } finally {
                this.saving = false;
            }
        },
        setCancellation(key: string, value: any) {
            this.policy = {
                ...this.policy,
                cancellation: { ...this.policy.cancellation, [key]: value },
            };
        },
        setCancellationNumber(key: string, value: any) {
            this.setCancellation(key, Number(value) || 0);
        },
        setCancellationFee(key: string, value: any) {
            this.setCancellation("fee", {
                ...this.policy.cancellation.fee,
                [key]: value,
            });
        },
        setCancellationFeeNumber(key: string, value: any) {
            this.setCancellationFee(key, Number(value) || 0);
        },
        setReturn(key: string, value: any) {
            this.policy = {
                ...this.policy,
                return: { ...this.policy.return, [key]: value },
            };
        },
        setReturnNumber(key: string, value: any) {
            this.setReturn(key, Number(value) || 0);
        },
        setReturnFee(key: string, value: any) {
            this.setReturn("restockingFee", {
                ...this.policy.return.restockingFee,
                [key]: value,
            });
        },
        setReturnFeeNumber(key: string, value: any) {
            this.setReturnFee(key, Number(value) || 0);
        },
        setReplacement(key: string, value: any) {
            this.policy = {
                ...this.policy,
                replacement: { ...this.policy.replacement, [key]: value },
            };
        },
        setReplacementNumber(key: string, value: any) {
            this.setReplacement(key, Number(value) || 0);
        },
        setRefund(key: string, value: any) {
            this.policy = {
                ...this.policy,
                refund: { ...this.policy.refund, [key]: value },
            };
        },
        setRefundNumber(key: string, value: any) {
            this.setRefund(key, Number(value) || 0);
        },
        setRefundDeduction(key: string, value: any) {
            this.policy = {
                ...this.policy,
                refund: {
                    ...this.policy.refund,
                    deductions: {
                        ...this.policy.refund.deductions,
                        [key]: value,
                    },
                },
            };
        },
        openMarketModal(market?: any, index: number = -1) {
            this.marketDraftIndex = index;
            this.marketNameError = "";
            if (market) {
                this.marketDraft = {
                    marketName: market.marketName || "",
                    conditions: market.conditions || "",
                    overrideCancellation: Boolean(market.cancellation),
                    overrideReturn: Boolean(market.return),
                    overrideReplacement: Boolean(market.replacement),
                    overrideRefund: Boolean(market.refund),
                    cancellation: clonePolicy(
                        market.cancellation || DEFAULT_POLICY.cancellation,
                    ),
                    return: clonePolicy(market.return || DEFAULT_POLICY.return),
                    replacement: clonePolicy(
                        market.replacement || DEFAULT_POLICY.replacement,
                    ),
                    refund: clonePolicy(market.refund || DEFAULT_POLICY.refund),
                };
            } else {
                this.marketDraft = {
                    marketName: "",
                    conditions: "",
                    overrideCancellation: false,
                    overrideReturn: false,
                    overrideReplacement: false,
                    overrideRefund: false,
                    cancellation: clonePolicy(DEFAULT_POLICY.cancellation),
                    return: clonePolicy(DEFAULT_POLICY.return),
                    replacement: clonePolicy(DEFAULT_POLICY.replacement),
                    refund: clonePolicy(DEFAULT_POLICY.refund),
                };
            }
            this.showMarketModal = true;
        },
        closeMarketModal() {
            this.showMarketModal = false;
            this.marketDraftIndex = -1;
        },
        setMarketDraft(key: string, value: any) {
            this.marketDraft = { ...this.marketDraft, [key]: value };
        },
        setMarketSection(section: string, key: string, value: any) {
            this.marketDraft = {
                ...this.marketDraft,
                [section]: {
                    ...(this.marketDraft as any)[section],
                    [key]: value,
                },
            };
        },
        setMarketSectionNumber(section: string, key: string, value: any) {
            this.setMarketSection(section, key, Number(value) || 0);
        },
        setMarketFeeType(section: string, feeKey: string, type: any) {
            this.marketDraft = {
                ...this.marketDraft,
                [section]: {
                    ...(this.marketDraft as any)[section],
                    [feeKey]: {
                        ...((this.marketDraft as any)[section][feeKey] || {}),
                        type,
                    },
                },
            };
        },
        setMarketFeeValue(section: string, feeKey: string, value: any) {
            this.marketDraft = {
                ...this.marketDraft,
                [section]: {
                    ...(this.marketDraft as any)[section],
                    [feeKey]: {
                        ...((this.marketDraft as any)[section][feeKey] || {}),
                        value: Number(value) || 0,
                    },
                },
            };
        },
        setMarketDeduction(key: string, value: any) {
            this.marketDraft = {
                ...this.marketDraft,
                refund: {
                    ...this.marketDraft.refund,
                    deductions: {
                        ...this.marketDraft.refund.deductions,
                        [key]: value,
                    },
                },
            };
        },
        saveMarketDraft() {
            const name = `${this.marketDraft.marketName || ""}`.trim();
            if (!name) {
                this.marketNameError = "Market name is required.";
                return;
            }
            const duplicated = this.policy.marketPolicies.some(
                (m: any, i: number) =>
                    i !== this.marketDraftIndex &&
                    `${m.marketName || ""}`.trim().toLowerCase() ===
                        name.toLowerCase(),
            );
            if (duplicated) {
                this.marketNameError = "Market name must be unique.";
                return;
            }

            const payload: any = { marketName: name };
            if (`${this.marketDraft.conditions || ""}`.trim())
                payload.conditions = `${this.marketDraft.conditions}`.trim();
            if (this.marketDraft.overrideCancellation)
                payload.cancellation = clonePolicy(
                    this.marketDraft.cancellation,
                );
            if (this.marketDraft.overrideReturn)
                payload.return = clonePolicy(this.marketDraft.return);
            if (this.marketDraft.overrideReplacement)
                payload.replacement = clonePolicy(this.marketDraft.replacement);
            if (this.marketDraft.overrideRefund)
                payload.refund = clonePolicy(this.marketDraft.refund);

            const next = clonePolicy(this.policy.marketPolicies);
            if (this.marketDraftIndex > -1)
                next.splice(this.marketDraftIndex, 1, payload);
            else next.push(payload);
            this.policy = { ...this.policy, marketPolicies: next };
            this.closeMarketModal();
        },
        async removeMarket(index: number) {
            const result = await (this as any).$swal({
                title: "Delete market override?",
                text: "This cannot be undone.",
                type: "warning",
                showCancelButton: true,
                confirmButtonText: "Delete",
            });
            if (!result.value) return;
            const next = clonePolicy(this.policy.marketPolicies);
            next.splice(index, 1);
            this.policy = { ...this.policy, marketPolicies: next };
        },
    },
});
